import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/useAuth';
import { AppProvider } from './context/AppContext';
import { TenantProvider } from './context/TenantContext';
import { PresenceProvider } from './context/PresenceContext';
import { ConfirmProvider } from './components/ConfirmModal';
import SessionTimeoutGuard from './components/SessionTimeoutGuard';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import { flushUsageEvents, trackUsageEvent } from './utils/usageTelemetry';
import { BUYER_MAP_PATH, canAccessBuyers, canAccessInvestors, canAccessPrivateCrm, defaultSignedInPath, isBuyerMapEnabled, isBuyerOnlyUser } from './utils/accessControl';
import BuyerMapShutDown from './pages/BuyerMapShutDown.jsx';
// Landing page removed — "/" now redirects to /login

// Eager only where the route is on the first-paint path for a normal
// session: /sign/:token is a public landing page that must not wait on
// a second round trip, and /onboarding is linked straight from the
// invite email.
// Three pages a staff member almost never opens, and a signer or a title
// agent opens once: out of the boot bundle. They sit inside the same
// <Suspense> as every lazy page below.
const SignDocument = lazy(() => import('./pages/SignDocument'));
const TitleChartShare = lazy(() => import('./pages/TitleChartShare'));
const Onboarding = lazy(() => import('./pages/Onboarding'));

// Heavy, seldom-all-needed-at-once pages load on demand. These five
// alone were shipping ~40% of the initial bundle; splitting them
// keeps the first paint cheap for users who land on /login or
// /deals.
const DocumentLibrary = lazy(() => import('./pages/DocumentLibrary'));
const Mailroom = lazy(() => import('./pages/Mailroom.jsx'));
const LeafletMapPage = lazy(() => import('./pages/MapPage'));
const MapboxMapPage = lazy(() => import('./pages/MapboxMapPage'));
const BuyerDealsMap = lazy(() => import('./pages/BuyerDealsMap.jsx'));
const TenantHome = lazy(() => import('./pages/TenantHome.jsx'));

const BuyerOutreach = lazy(() => import('./pages/BuyerOutreach.jsx'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const DealsDashboard = lazy(() => import('./pages/DealsDashboard'));
const Home = lazy(() => import('./pages/Home'));
const PrivateCrm = lazy(() => import('./pages/PrivateCrm'));
const SellYourMinerals = lazy(() => import('./pages/SellYourMinerals'));

// Role- or task-specific routes. Every user was downloading all of
// these to reach any one of them; Admin and Accounting in particular
// are staff-only screens sitting in the path of a landman opening
// /deals on a phone. They are behind the same <Suspense> as the pages
// above, so a lazy boundary costs nothing extra here.
const Buyers = lazy(() => import('./pages/Buyers'));
const Investors = lazy(() => import('./pages/Investors'));
const Closings = lazy(() => import('./pages/Closings'));
const Admin = lazy(() => import('./pages/Admin'));
const Accounting = lazy(() => import('./pages/Accounting'));
const AssetsUnderManagement = lazy(() => import('./pages/AssetsUnderManagement'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));

function MapPage() {
  const useMapbox = import.meta.env.VITE_MAP_ENGINE !== 'leaflet';
  return useMapbox ? <MapboxMapPage /> : <LeafletMapPage />;
}

function isMapHeavyPath(pathname = '') {
  return pathname === '/map'
    || pathname === '/map-leaflet'
    || pathname === '/map-only';
}

/* Route guard: must be logged in */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (isBuyerOnlyUser(user) && location.pathname !== BUYER_MAP_PATH) {
    return <Navigate to={BUYER_MAP_PATH} replace />;
  }
  return children;
}

// There was an OnboardingRoute here. It wrapped every app route and
// bounced a newly invited agent to /onboarding until their contractor
// agreement, W-9 and direct deposit had all come back signed — so the
// first thing a new hire met was a demand for their SSN and bank
// account, before they had seen a screen of the thing they were hired
// to use. The paperwork is still collected and still tracked; it is a
// banner now (see Layout), and what enforces it is the payout, which
// cannot be made without it. Its buyer-only redirect was already
// ProtectedRoute's job, so nothing else was lost with it.

// The map routes used to sit behind an AppDataReadyRoute that held the
// whole page — search box included — until deals, contacts, staff and
// buyers had all loaded. The map pages read those with [] defaults and
// re-render when they land, so the wait bought nothing and put the
// entire app's data ahead of a search the user came here to type.

function PrivateCrmRoute() {
  const { user } = useAuth();
  if (!canAccessPrivateCrm(user)) return <Navigate to="/deals" replace />;
  return <Page name="Lead Center"><PrivateCrm /></Page>;
}

// Admin-only pages (Accounting ledger, AUM). The components also gate
// themselves — this is the route-level lock so a non-admin never even
// mounts them.
function AdminOnlyRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/deals" replace />;
  return children;
}

// The Buyers roster is admin-only — see canAccessBuyers. Gated at the
// route as well as in the nav so typing the URL is not a way around
// it, and so a non-admin never mounts the page or its data.
function BuyersRoute() {
  const { user } = useAuth();
  if (!canAccessBuyers(user)) return <Navigate to="/deals" replace />;
  return <Page name="Buyers"><Buyers /></Page>;
}

// The investor roster carries commission rates and ownership splits, so
// it is admin-only — see canAccessInvestors. Gated at the route as well
// as in the nav, for the same reason Buyers is.
function InvestorsRoute() {
  const { user } = useAuth();
  if (!canAccessInvestors(user)) return <Navigate to="/deals" replace />;
  return <Page name="Investors"><Investors /></Page>;
}

/**
 * Where signing in lands you: the home page, always.
 *
 * The Oklahoma tenant used to land on its own hub instead, which meant
 * half the team never saw the home page — its desk panel, its tools,
 * and the "What's new" rail that is the only place a shipped feature
 * gets explained. The hub is still one click away in the nav.
 *
 * A buyer-only profile is the one exception, and not really an
 * exception: /home is a staff route that would bounce them straight
 * back out, so they go where they can actually be.
 */
function signedInPathForTenant(user) {
  if (isBuyerOnlyUser(user)) return BUYER_MAP_PATH;
  return defaultSignedInPath(user);
}

// Wraps a lazy/heavy route in a per-route ErrorBoundary so a crash
// inside that page doesn't take down the whole app shell. Without
// this, a single bad render in (say) MapPage propagates up to the
// top-level Sentry boundary and the user loses their nav too.
const Page = ({ name, children }) => (
  <RouteErrorBoundary routeName={name}>{children}</RouteErrorBoundary>
);

function UsageTelemetry() {
  const { user } = useAuth();
  const location = useLocation();
  const routeRef = useRef(null);
  const enteredAtRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const now = Date.now();
    const previousRoute = routeRef.current;
    if (previousRoute && enteredAtRef.current != null) {
      trackUsageEvent('page_duration', {
        category: 'performance',
        route: previousRoute,
        durationMs: now - enteredAtRef.current,
      });
    }
    routeRef.current = location.pathname;
    enteredAtRef.current = now;
    trackUsageEvent('route_view', {
      category: 'navigation',
      route: location.pathname,
      metadata: { hasSearch: Boolean(location.search) },
    });
  }, [location.pathname, location.search, user]);

  useEffect(() => {
    if (!user) return undefined;
    const trackWindowError = () => {
      trackUsageEvent('app_error', {
        category: 'error',
        route: routeRef.current,
        target: 'window_error',
      });
    };
    const trackUnhandledRejection = () => {
      trackUsageEvent('app_error', {
        category: 'error',
        route: routeRef.current,
        target: 'unhandled_rejection',
      });
    };
    const flushCurrentPage = () => {
      const now = Date.now();
      if (routeRef.current && enteredAtRef.current != null) {
        trackUsageEvent('page_duration', {
          category: 'performance',
          route: routeRef.current,
          durationMs: now - enteredAtRef.current,
        });
      }
      enteredAtRef.current = now;
      flushUsageEvents();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushCurrentPage();
    };

    window.addEventListener('error', trackWindowError);
    window.addEventListener('unhandledrejection', trackUnhandledRejection);
    window.addEventListener('beforeunload', flushCurrentPage);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('error', trackWindowError);
      window.removeEventListener('unhandledrejection', trackUnhandledRejection);
      window.removeEventListener('beforeunload', flushCurrentPage);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user]);

  return null;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const signedInPath = signedInPathForTenant(user);
  const rootSearchParams = new URLSearchParams(location.search || '');
  const rootIsOAuthCallback = location.pathname === '/'
    && (rootSearchParams.has('code') || rootSearchParams.has('error') || rootSearchParams.has('error_description'));

  return (
    <>
    <UsageTelemetry />
    <SessionTimeoutGuard />
    <Suspense fallback={<LoadingScreen autoReload={false} />}>
      <Routes>
      {/* Public — landing redirects straight to login */}
      <Route path="/" element={rootIsOAuthCallback ? <AuthCallback /> : <Navigate to={user ? signedInPath : '/login'} />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/demo/:tenantId/:token" element={<Page name="Tenant Demo"><TenantHome preview /></Page>} />
      <Route path="/login" element={user ? <Navigate to={signedInPath} /> : <Login />} />
      {/* Public e-sign page — token in the URL is the credential. */}
      <Route path="/sign/:token" element={<SignDocument />} />
      {/* The confirmation-title chart, for a Shadowland Title agent with no
          account here. Same rule as the signing page: the token in the URL
          is the whole credential, and it reaches one deal's chain of title
          and nothing else. */}
      <Route path="/title-chart/:token" element={<TitleChartShare />} />
      {/* Public lead page: owners request an offer, the form lands in
          the Private CRM tagged "Website Lead". */}
      <Route path="/sell" element={<SellYourMinerals />} />
      {/* No /buyer-map route here on purpose: AppEntry short-circuits
          that exact path to PublicBuyerMapRoute *before* AuthProvider
          mounts, so the buyer map never pays for app auth. A duplicate
          route here would be unreachable. */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Page name="Onboarding"><Onboarding /></Page>
        </ProtectedRoute>
      } />
      {/* Split-screen map — full map without sidebar */}
      <Route path="/map-only" element={
        <ProtectedRoute>
          <AppProvider deferHeavyHydration>
            <div className="main-content" style={{ width: '100vw', height: '100vh' }}>
                <Page name="Map"><MapPage /></Page>
            </div>
          </AppProvider>
        </ProtectedRoute>
      } />

      {/* Dashboard — app staff only; buyer-only profiles stay on /buyer-map. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppProvider deferHeavyHydration={isMapHeavyPath(location.pathname)}>
              <PresenceProvider>
                <Layout />
              </PresenceProvider>
            </AppProvider>
          </ProtectedRoute>
        }
      >
        {/* Assignments are gone — bookmarks / old links land on the Deal Tracker */}
        <Route path="assignments/*" element={<Navigate to="/deals" replace />} />
        {/* Flowchart editor was removed — any bookmarks / old links
            land on the Deal Tracker. Existing flowchart data is
            still readable via FlowchartViewerModal in the per-deal
            Documents card. */}
        <Route path="flowcharts/*" element={<Navigate to="/deals" replace />} />
        <Route path="documents" element={<Page name="Document Library"><DocumentLibrary /></Page>} />
        {/* Meant to be left open on a screen by the printer, so it is a
            route of its own rather than a panel inside something else. */}
        <Route path="mailroom" element={<Page name="Mailroom"><Mailroom /></Page>} />
        <Route path="tenant-home" element={<Page name="Tenant Home"><TenantHome /></Page>} />
        <Route path="map" element={<Page name="Map"><MapPage /></Page>} />
        <Route path="marketing-map" element={<Navigate to="/map" replace />} />
        <Route path="map-leaflet" element={<Page name="Map"><LeafletMapPage /></Page>} />
        <Route path="pipeline" element={<Navigate to="/deals" replace />} />
        <Route path="pipeline/:id" element={<Navigate to="/deals" replace />} />
        <Route path="home" element={<Page name="Home"><Home /></Page>} />
        <Route path="deals" element={<Page name="Deals"><DealsDashboard /></Page>} />
        <Route path="crm" element={<PrivateCrmRoute />} />
        <Route path="owners/:contactId/dashboard" element={<Page name="Owner Dashboard"><OwnerDashboard /></Page>} />
        <Route path="buyers" element={<BuyersRoute />} />
        <Route path="investors" element={<InvestorsRoute />} />
        <Route path="outreach" element={<Page name="Buyer Outreach"><BuyerOutreach /></Page>} />
        <Route path="closings" element={<Page name="Closings"><Closings /></Page>} />
        <Route path="title-requests" element={<Navigate to="/deals" />} />
        <Route path="admin" element={<Page name="Admin"><Admin /></Page>} />
        <Route path="accounting" element={<AdminOnlyRoute><Page name="Accounting"><Accounting /></Page></AdminOnlyRoute>} />
        <Route path="assets-under-management" element={<AdminOnlyRoute><Page name="Assets Under Management"><AssetsUnderManagement /></Page></AdminOnlyRoute>} />
        <Route path="notification-settings" element={<Page name="Notification Settings"><NotificationSettings /></Page>} />
      </Route>

      <Route path="*" element={<Navigate to={user ? signedInPath : '/'} />} />
      </Routes>
    </Suspense>
    </>
  );
}

function PublicBuyerMapRoute() {
  // Shut down while the buyer share link is treated as compromised.
  // Rendered eagerly so the map chunk — and its data fetches — never
  // load; the API routes refuse independently, this just avoids
  // handing a leaked link a broken map instead of an explanation.
  if (!isBuyerMapEnabled()) return <Page name="Buyer Map"><BuyerMapShutDown /></Page>;
  return (
    <Suspense fallback={<LoadingScreen autoReload={false} />}>
      <Page name="Buyer Map"><BuyerDealsMap /></Page>
    </Suspense>
  );
}

function AppEntry() {
  const location = useLocation();
  if (location.pathname === BUYER_MAP_PATH) return <PublicBuyerMapRoute />;
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <TenantProvider>
        <ConfirmProvider>
          <AppEntry />
        </ConfirmProvider>
      </TenantProvider>
    </BrowserRouter>
  );
}
